// EPG provider-alias map. Used by `useEpgStore` + `catchupEpgFallback` to
// bridge live-side prefixes that don't have a 1:1 EPG-side prefix in the
// user's enabled sources, but DO have a known sibling prefix that the same
// broadcaster's catalogue is exposed under.
//
// Empirical evidence: `scripts/epg-coverage-snapshot.mjs` (and its planning
// predecessor `scripts/epg-alias-discovery.mjs`) walks the user's
// `data/content.db` and, for every live channel that doesn't strict-match,
// counts which EPG-side prefix the bare-key would N=1-resolve to. Entries
// in this map come from those tallies — see the table in the plan at
// `/Users/schubmma/.claude/plans/normalizer-extension-cosmic-walrus.md`.
//
// Mirrored byte-equivalently at `server/services/epgAliases.js`. The
// pair-contract test in `tests/unit/epgAliasesPair.test.js` fails the build
// if the two parse to different values.
//
// Phase post-4.10 — SCHEMA VERSION 1.
//
// SECURITY: pure static data, no I/O, no log, no Pinia. The map is loaded
// once at module-level; consumers iterate the result of
// `expandPrefixAliases(livePrefix)`. No user-supplied data ever flows into
// the alias-resolution path.

export const PROVIDER_ALIAS_MAP: Record<string, string[]> = {
    UK: ['VIP', 'NOW'],
    DE: ['AT', 'SKYGO', 'WOW'],
    FR: ['VIP'],
    '4K': ['VIP', 'NOW', 'UK', 'TV'],
    '8K': ['UK'],
    NOW: ['VIP', 'UK'],
    SKYGO: ['DE', 'AT', 'WOW'],
    OD: ['NL'],
    CG: ['SR'],
    MEO: ['PT', 'VIP'],
    JOYN: ['DE'],
    'PLAY+': ['NL', 'DE'],
    LA: ['US'],
    GO: ['US', 'TV'],
    NM: ['BE'],
};

/**
 * Return the ordered prefix list to try when looking up an EPG entry for
 * a live channel whose extracted country prefix is `livePrefix`. The live
 * prefix is ALWAYS the first entry (so strict-match precedence is
 * preserved — tier-2 of the resolver runs the prefix's own strict lookup
 * before consulting aliases). The remaining entries are the empirically-
 * derived siblings.
 *
 * Returns `[livePrefix]` (one element, the prefix itself) when the live
 * prefix is not present in the alias map. That lets the caller iterate
 * uniformly without a presence check.
 *
 * Case-insensitive on the input: `expandPrefixAliases('uk')` returns the
 * same list as `expandPrefixAliases('UK')`. The output is always
 * uppercased.
 */
export function expandPrefixAliases(livePrefix: unknown): string[] {
    if (typeof livePrefix !== 'string' || livePrefix.length === 0) return [];
    const up = livePrefix.toUpperCase();
    const aliases = PROVIDER_ALIAS_MAP[up];
    if (!aliases) return [up];
    return [up, ...aliases];
}
